import { Database } from 'lucide-react'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = (
  <Database className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

export function VendorsListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count =
    typeof state.result?.count === 'number' ? state.result.count : null
  const vendors: Array<Record<string, unknown>> = Array.isArray(
    state.result?.vendors
  )
    ? state.result.vendors
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading vendors...'
        completedLabel={
          count !== null
            ? `${plural(count, 'vendor')} loaded`
            : 'Vendors loaded'
        }
        completedDetail={pickString(state.args?.tier, state.args?.category)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No vendors found'
          items={vendors.map((vendor) => ({
            id: pickString(vendor.displayId),
            title: pickString(vendor.name) ?? 'Unnamed vendor',
            subtitle: pickString(vendor.service, vendor.ownerLabel),
            meta: [vendor.riskTier, vendor.status, vendor.category],
            tone: normalizeTone(vendor.riskTier),
          }))}
        />
      ) : null}
    </>
  )
}

export function VendorsGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading vendor...'
        completedLabel='Vendor loaded'
        loadingDetail={pickString(state.args?.vendorId)}
        completedDetail={pickString(state.result?.name, state.args?.vendorId)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) && state.result ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `vendor-${pickString(state.result.displayId, state.args?.vendorId) ?? 'detail'}`
          )}
          payload={{
            entityType: 'vendor',
            toolName: 'vendorsGet',
            title: pickString(state.result.name) ?? 'Unnamed vendor',
            subtitle: pickString(state.result.displayId, state.args?.vendorId),
            description: pickString(
              state.result.service,
              state.result.description
            ),
            fields: [
              {
                label: 'Risk',
                value: formatToolValue(state.result.riskTier),
                tone: normalizeTone(state.result.riskTier),
              },
              {
                label: 'Residual',
                value: formatToolValue(state.result.residualRiskTier),
                tone: normalizeTone(state.result.residualRiskTier),
              },
              {
                label: 'Status',
                value: formatToolValue(state.result.status),
              },
              {
                label: 'Category',
                value: formatToolValue(state.result.category),
              },
              {
                label: 'Owner',
                value: formatToolValue(state.result.ownerLabel),
              },
              {
                label: 'Assessment',
                value: formatToolValue(state.result.latestAssessment?.status),
              },
            ],
          }}
          systemDetail={
            pickString(state.result.id, state.args?.vendorId)
              ? {
                  entityType: 'vendor',
                  entityId: pickString(state.result.id, state.args?.vendorId)!,
                  workspaceId: '',
                }
              : null
          }
        />
      ) : null}
    </>
  )
}

export function VendorsCreateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Adding vendor...'
      completedLabel='Vendor added'
      loadingDetail={pickString(state.args?.name)}
      completedDetail={pickString(
        state.result?.displayId,
        state.result?.name,
        state.args?.name
      )}
      completedIcon={completedIcon}
    />
  )
}

export function VendorsAssessToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const required: string[] = Array.isArray(state.result?.requiredEvidence)
    ? state.result.requiredEvidence.filter(
        (item: unknown): item is string => typeof item === 'string'
      )
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Drafting vendor assessment...'
        // Never render this as "assessed" / "reviewed". The tool creates a
        // DRAFT and the maker-checker gate leaves approval with a human — a
        // completed label implying otherwise would misread at a glance.
        completedLabel='Draft assessment created'
        loadingDetail={pickString(state.args?.vendorId)}
        completedDetail={pickString(
          state.result?.vendor?.name,
          state.args?.vendorId
        )}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No evidence requested'
          items={required.map((label, index) => ({
            id: `req-${index}`,
            title: label,
            meta: ['Required'],
          }))}
        />
      ) : null}
    </>
  )
}

export function VendorsRequestEvidenceToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const outstanding: Array<Record<string, unknown>> = Array.isArray(
    state.result?.outstanding
  )
    ? state.result.outstanding
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Checking outstanding vendor evidence...'
        completedLabel={
          state.isComplete && !isToolError(state)
            ? outstanding.length === 0
              ? 'Nothing outstanding'
              : `${plural(outstanding.length, 'item')} outstanding`
            : 'Evidence checked'
        }
        loadingDetail={pickString(state.args?.vendorId)}
        completedDetail={pickString(
          state.result?.vendor?.name,
          state.args?.vendorId
        )}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No outstanding evidence'
          items={outstanding.map((item, index) => ({
            id: `outstanding-${index}`,
            title: pickString(item.label) ?? 'Evidence item',
            subtitle: pickString(item.docType),
            meta: [item.required ? 'Required' : 'Optional'],
            tone: item.required ? ('medium' as const) : undefined,
          }))}
        />
      ) : null}
    </>
  )
}

export function VendorsSearchToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count =
    typeof state.result?.count === 'number' ? state.result.count : null
  const vendors: Array<Record<string, unknown>> = Array.isArray(
    state.result?.vendors
  )
    ? state.result.vendors
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Searching vendors...'
        completedLabel={
          count !== null
            ? `Found ${plural(count, 'vendor')}`
            : 'Vendor search complete'
        }
        loadingDetail={pickString(state.args?.query)}
        completedDetail={count === 0 ? null : pickString(state.args?.query)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No matching vendors'
          items={vendors.map((vendor) => ({
            id: pickString(vendor.displayId),
            title: pickString(vendor.name) ?? 'Unnamed vendor',
            subtitle: pickString(vendor.service, vendor.ownerLabel),
            meta: [vendor.riskTier, vendor.status, vendor.category],
            tone: normalizeTone(vendor.riskTier),
          }))}
        />
      ) : null}
    </>
  )
}
